import clsx from 'clsx'
import { createContext, useContext } from 'react'
import { useUiFromSpecContext } from '../EditorContextProvider'
import { getElementByKey } from './utils/getElementByKey'
import { RenderView } from './RenderView'
import { UseFormReturn } from 'react-hook-form'
import { useCallback, useRef } from 'react'
import { useDrag } from 'react-dnd'
import { ItemTypes } from '../Editor/dndTypes'
import { MdDragIndicator } from 'react-icons/md'
import { containsMultiXPath } from '../Editor/Panels/utils/containsMultiXPath'

type RenderUiFromSpecProps = {
  item: any
  form: UseFormReturn<any>
  onSubmit?: (data: any) => void
  xpath: string
  index: number
  title: string
  inputSize: any
  className: any
  children: any
  style?: any
}

const RenderUiFromSpec = (props: RenderUiFromSpecProps) => {
  const {
    editor,
    currentSpec,
    setSelectedElement,
    setSelectedPath,
    selectedPath,
    setShow,
    selectedElements,
    reorderElementsInSpec,
    reorderMultipleElementsInSpec,
    cannotDragElements,
    customDropper,
    customReOrder,
  } = useUiFromSpecContext()
  const { xpath, item } = props

  const canDragThis = !cannotDragElements?.includes(item?.ui_type)

  const [{ isDragging }, connectDrag, connectDragPreview] = useDrag(
    () => ({
      type: ItemTypes.CANVAS_ELEMENT,
      item: { ...item, originalXpath: xpath },
      canDrag: canDragThis,
      end: (_item, monitor) => {
        const dropResult = monitor.getDropResult<{ xpath: string; index: number; uiType?: string; fromDropper?: boolean; targetComponent?: any }>()
        if (!dropResult) return

        // AddChildItem renders *after* item at index N and returns index=N,
        // meaning "insert after N" → target position is N+1.
        // ChildDropper in LayerPanel already sends the correct insert position directly.
        const insertIndex = dropResult.fromDropper ? dropResult.index + 1 : dropResult.index
        const toIndex = dropResult.xpath + '.' + insertIndex

        // Allow if it's a dedicated dropper (AddChildItem / ChildDropper) OR a valid container
        const isValidDrop = dropResult.fromDropper || (dropResult.uiType && customDropper?.includes(dropResult.uiType))
        if (!isValidDrop) return

        if (dropResult.xpath.startsWith(xpath + '.')) return

        if (customReOrder) {
          customReOrder({
            item: _item,
            dropResult,
            selectedElements,
            fromIndex: xpath.split('.'),
            toIndex: toIndex.split('.'),
            defaultReOrder: reorderElementsInSpec,
            defaultMultipleReOrder: reorderMultipleElementsInSpec,
          })
        } else if (
          selectedElements &&
          selectedElements.length > 0 &&
          containsMultiXPath(selectedElements, xpath.split('.'))
        ) {
          reorderMultipleElementsInSpec(selectedElements, toIndex.split('.'))
        } else {
          reorderElementsInSpec(xpath.split('.'), toIndex.split('.'))
        }
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [xpath, item, selectedElements, canDragThis]
  )

  const dragHandleRef = useCallback(
    (el: HTMLDivElement | null) => { if (canDragThis) connectDrag(el) },
    [connectDrag, canDragThis]
  )

  const previewRef = useRef<HTMLDivElement>(null)
  connectDragPreview(previewRef)

  /**
   * Handle selection of the current element
   */
  const handleElementSelection = (event: React.MouseEvent, showPanel = false) => {
    event.stopPropagation()
    const element = getElementByKey(currentSpec ?? [], xpath)

    if (element != null) {
      setSelectedElement?.(element)
      setSelectedPath?.(xpath)

      if (showPanel) {
        setShow?.(true)
      }
    }
  }

  const isSelected = (selectedElements ?? []).some((x) => x.join('.') === xpath) || selectedPath === xpath

  // Classes for the editor container
  const editorContainerClasses = clsx(
    'flex flex-col p-2 border-dashed rounded-md editor-container absolute top-0 left-0 w-full h-full',
    isSelected ? 'border-blue-500 border-2' : 'border-gray-alurkerja-2 border'
  )

  // Preview panel override: force view-only regardless of context editor flag
  const isPreviewPanel = useContext(PreviewModeContext)

  // If not in editor mode (or forced view-only for split preview), just render the view
  if (!editor || isPreviewPanel) {
    return <RenderView {...props} />
  }

  // In editor mode, add selection + drag capabilities
  return (
    <div
      ref={previewRef}
      className={clsx(
        'relative w-full h-full group/canvas p-2',
        isDragging && 'opacity-40'
      )}
    >
      <div
        data-key={xpath}
        className={editorContainerClasses}
        onMouseEnter={(event) => handleElementSelection(event)}
        onClick={(event) => handleElementSelection(event, true)}
      />
      {/* Canvas drag handle — sisi kiri, float absolut, tidak mempengaruhi layout */}
      {canDragThis && (
        <div
          ref={dragHandleRef}
          className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-5 h-5 rounded bg-white border border-slate-300 text-slate-500 hover:text-slate-800 hover:border-slate-500 shadow opacity-0 group-hover/canvas:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
          title="Drag to move"
          onClick={(e) => e.stopPropagation()}
        >
          <MdDragIndicator className="text-sm" />
        </div>
      )}
      <RenderView {...props} />
    </div>
  )
}

export { RenderUiFromSpec }
export type { RenderUiFromSpecProps }

/** Set to true for any RenderUi subtree that should render as pure preview (no editor overlays). */
export const PreviewModeContext = createContext(false)
